import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaStar, FaMountain } from 'react-icons/fa';
import { companyInfo } from '../../data/tourData';
import BookingForm from '../../components/common/BookingForm';
import './TourDetail.css';

const KuariPass = () => {
  const tourData = {
    name: "Kuari Pass Trek",
    duration: "04 Nights 05 Days",
    price: "₹ 6,999/-",
    image: "https://res.cloudinary.com/duym2bgp2/image/upload/v1754935181/wrfiog0aowtrhxr4dz7n.jpg",
    altitude: "13,900 ft",
    difficulty: "Moderate",
    region: "Uttarakhand",
    highlights: ["Ancient route", "Mountain passes", "Sacred sites"],
    bestTime: "March to June, September to November",
    description: "The Kuari Pass Trek is an ancient pilgrimage route that offers stunning views of the Garhwal Himalayas. This moderate trek takes you through beautiful meadows, dense forests, and high mountain passes with panoramic views of sacred peaks."
  };

  return (
    <>
      <Helmet>
        <title>Kuari Pass Trek - NomadTrailz</title>
        <meta name="description" content="Experience the ancient Kuari Pass Trek in Uttarakhand. Sacred mountain passes, panoramic views, and spiritual journey through the Garhwal Himalayas." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <section className="tour-hero">
          <div className="hero-background">
            <img
              src={tourData.image}
              alt={tourData.name}
            />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {tourData.name}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              An ancient pilgrimage route through sacred mountain passes
            </motion.p>
          </div>
        </section>

        <section className="tour-details">
          <div className="container">
            <div className="details-grid">
              <div className="tour-info">
                <motion.div
                  className="info-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h2>Tour Overview</h2>
                  <p>{tourData.description}</p>
                  <p>
                    This trek follows the ancient route used by pilgrims and traders, offering a perfect blend 
                    of adventure and spirituality. The Kuari Pass provides breathtaking views of sacred peaks 
                    like Nanda Devi, Trishul, and Dronagiri.
                  </p>
                </motion.div>

                <motion.div
                  className="highlights-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Highlights</h3>
                  <ul>
                    <li>Ancient pilgrimage route</li>
                    <li>Panoramic views of sacred peaks</li>
                    <li>Beautiful alpine meadows</li>
                    <li>Dense rhododendron forests</li>
                    <li>Traditional mountain villages</li>
                    <li>Spiritual significance</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="itinerary-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Itinerary</h3>
                  <div className="day-item">
                    <h4>Day 1: From Rishikesh to Joshimath</h4>
                    <ul>
                      <li>Today we will start our journey in the early morning for Joshimath.</li>
                      <li>The town, which is located in the Chamoli District in the state of Uttarakhand in India, will serve as the starting point for the journey to the magnificent Kuari Pass.</li>
                      <li>There are a few Himalayan trekking places that may be reached via this location, which is situated at an elevation of 6,150 feet.</li>
                      <li>As you make your way to Joshimath, you will go through the Panchprayag, also known as the five auspicious junctures of the Ganga.</li>
                      <li>These junctures include Devaprayag, Rudraprayag, Karnaprayag, Nandaprayag, and Vishnuprayag.</li>
                      <li>Keep your cameras close at hand so that you can capture the breathtaking scenery of the mountains.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 2: From Joshimath to Tali</h4>
                    <ul>
                      <li>After breakfast, you will begin your trek today, which will take you from Joshimath to Dakh to Gulling Top.</li>
                      <li>The morning in the Himalayas, with its crisp air and serene surroundings, will fill you with excitement.</li>
                      <li>In the beginning of the difficult rise, you will start from Joshimath.</li>
                      <li>You will see the enormous mountains in front of you, including the peak that is known as Sleeping Lady Peak.</li>
                      <li>As you continue on your journey, you will first have to traverse Tugasi village, where you will get a close look at the culture and traditions of the highlands.</li>
                      <li>After around twenty to twenty-five minutes of trekking, you will arrive at Kharchi, where you will be able to replenish your water bottles.</li>
                      <li>Eventually you will arrive at Tali, which is your home for the day.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 3: Tali to Kuari Pass Base Camp</h4>
                    <ul>
                      <li>Today involves trekking from Tali to Kuari Pass Base Camp.</li>
                      <li>The trail becomes more challenging with steep ascents as you gain elevation.</li>
                      <li>You'll encounter snow-covered patches and alpine meadows along the way.</li>
                      <li>The views become more spectacular as you climb higher into the mountains.</li>
                      <li>Camp at Kuari Pass Base Camp, experiencing the high-altitude environment.</li>
                      <li>Prepare for the pass crossing the next day.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 4: Kuari Pass Base Camp to Kuari Pass and Back to Tali</h4>
                    <ul>
                      <li>The most exciting day of the trek! Early morning start for the pass crossing.</li>
                      <li>You'll reach the Kuari Pass at 13,900 feet, offering breathtaking panoramic views.</li>
                      <li>The pass provides stunning views of the Himalayan ranges dominated by Nanda Devi peak, Kamet and Dronagiri.</li>
                      <li>This is one of the most rewarding and satisfying moments of the trek.</li>
                      <li>After spending time at the pass, descend back to Tali for the night.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 5: Tali to Joshimath and Drive to Rishikesh</h4>
                    <ul>
                      <li>The final day involves descending back to Joshimath, completing the trek.</li>
                      <li>Enjoy the descent through the same scenic routes you climbed.</li>
                      <li>Arrive back in Joshimath and board the vehicle for the return journey to Rishikesh.</li>
                      <li>Travel back through the same scenic route, reminiscing about the incredible adventure.</li>
                      <li>Arrive in Rishikesh with memories of an unforgettable Himalayan trek.</li>
                    </ul>
                  </div>
                </motion.div>

                <motion.div
                  className="things-to-carry-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Things To Carry</h3>
                  <p className="carry-intro">All items listed are essential, unless marked as optional. Items are divided into five categories for your convenience.</p>
                  
                  <div className="carry-categories">
                    <div className="carry-category">
                      <h4><i className="fas fa-hiking"></i> Trekking Gear</h4>
                      <ul>
                        <li>Rucksack with rain cover</li>
                        <li>Day pack bag</li>
                        <li>Head torch with spare batteries</li>
                        <li>UV protection sunglasses</li>
                        <li>Water bottles: Two 1-liter bottles</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-shoe-prints"></i> Footwear</h4>
                      <ul>
                        <li>Non-skid, deep treaded, high-ankle trekking shoes: 1 pair</li>
                        <li>Lightweight slippers or sandals: 1 pair</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-tshirt"></i> Clothing</h4>
                      <ul>
                        <li>Quick-dry warm lower or track pants: 2</li>
                        <li>Full-sleeve T-shirts/sweatshirts: 1 for every 2 days of trekking</li>
                        <li>Thick woolen socks: 1 pair for every 2 days of trekking</li>
                        <li>Thermal body warmer (upper & lower): 1 set</li>
                        <li>Undergarments: 1 for each day of trekking</li>
                        <li>Warm jacket (closed at wrist & neck): 1</li>
                        <li>Full-sleeve sweater: 1</li>
                        <li>Rainwear (jacket & pants): 1 set</li>
                        <li>Waterproof, warm gloves: 1 pair</li>
                        <li>Woolen cap: 1</li>
                        <li>Sun-shielding hat: 1</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-soap"></i> Toiletries</h4>
                      <ul>
                        <li>Personal toiletries kit: Small towel, toilet paper, paper soap, bar soap, toothbrush, toothpaste, cold cream, etc.</li>
                        <li>Sunscreen lotion: Small pack</li>
                        <li>Lip balm: Small pack</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-utensils"></i> Utensils</h4>
                      <ul>
                        <li>Small, lightweight, leak-proof lunch box: 1</li>
                        <li>Plate: 1</li>
                        <li>Spoon: 1</li>
                        <li>Tea/coffee mug (plastic): 1</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-plus-circle"></i> Miscellaneous</h4>
                      <ul>
                        <li className="optional">Camera: (Optional)</li>
                        <li>Personal medicines: Bring plenty if needed; consult your doctor before the trek.</li>
                        <li className="optional">Dry fruits, nuts, chocolate bars (Optional)</li>
                      </ul>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="inclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Inclusions</h3>
                  <ul>
                    <li>Transportation support on Traveller, Ertiga, or Innova (depending on the size of the group) throughout the journey</li>
                    <li>Accommodation on sharing basis</li>
                    <li>All meals (Vegetarian)</li>
                    <li>A well-experienced and certified trek leader</li>
                    <li>Cook</li>
                    <li>Supporting staff</li>
                    <li>Forest permit</li>
                    <li>Camping equipment (tents, sleeping bags, mattresses)</li>
                    <li>Kitchen and dining tent with all necessary utensils</li>
                    <li>Porters for carrying common equipment</li>
                    <li>Safety equipment and communication devices</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="exclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 1.0 }}
                >
                  <h3>Exclusions</h3>
                  <ul>
                    <li>Anything that has not been covered in the inclusions mentioned above</li>
                    <li>Porters or mules to carry your personal luggage. The cost for this service is 300 per day per bag (10/12 kg)</li>
                    <li>Any kind of personal expenses</li>
                    <li>Any kind of personal medication</li>
                    <li>Transportation to and from Rishikesh</li>
                    <li>Personal trekking gear and equipment</li>
                    <li>Any additional expenses due to weather conditions or unforeseen circumstances</li>
                  </ul>
                </motion.div>
              </div>

              <div className="booking-section">
                <motion.div
                  className="booking-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h3>Book This Tour</h3>
                  <div className="price-info">
                    <span className="price">{tourData.price}</span>
                    <span className="duration">{tourData.duration}</span>
                  </div>

                  <div className="tour-features">
                    <div className="feature">
                      <FaMapMarkerAlt />
                      <span>{tourData.region}</span>
                    </div>
                    <div className="feature">
                      <FaCalendarAlt />
                      <span>Best Time: {tourData.bestTime}</span>
                    </div>
                    <div className="feature">
                      <FaUsers />
                      <span>Group Size: 8-12 people</span>
                    </div>
                    <div className="feature">
                      <FaStar />
                      <span>Difficulty: {tourData.difficulty}</span>
                    </div>
                    <div className="feature">
                      <FaMountain />
                      <span>Altitude: {tourData.altitude}</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="quick-info-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Quick Info</h3>
                  <div className="info-item">
                    <FaMountain />
                    <div>
                      <strong>Max Altitude:</strong>
                      <span>13,900 feet</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaMapMarkerAlt />
                    <div>
                      <strong>Trek Distance:</strong>
                      <span>33 km (round trip)</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaStar />
                    <div>
                      <strong>Difficulty Level:</strong>
                      <span>Moderate</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaCalendarAlt />
                    <div>
                      <strong>Best Season:</strong>
                      <span>March to June, September to November</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="cost-details-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Cost Details</h3>
                  <h4>Cost Per Person (Ex. Rishikesh)</h4>
                  <ul>
                    <li><strong>Quad Sharing:</strong> ₹ 6,999 + 5% GST</li>
                    <li><strong>Triple Sharing:</strong> ₹ 7,499 + 5% GST</li>
                    <li><strong>Double Sharing:</strong> ₹ 7,999 + 5% GST</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="pickup-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Pickup Points</h3>
                  <ul>
                    <li><strong>Rishikesh:</strong> Rishikesh railway station between 06:00 AM to 07:00 AM</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="tour-highlights-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Tour Highlights</h3>
                  <ul>
                    <li>Ancient pilgrimage route</li>
                    <li>Panoramic views of sacred peaks</li>
                    <li>Beautiful alpine meadows</li>
                    <li>Dense rhododendron forests</li>
                    <li>Traditional mountain villages</li>
                    <li>Spiritual significance</li>
                    <li>Nanda Devi, Kamet and Dronagiri views</li>
                    <li>Professional trek leader and support staff</li>
                  </ul>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <BookingForm 
                  tourName={tourData.name}
                  tourPrice={tourData.price}
                  tourDuration={tourData.duration}
                />
              </motion.div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default KuariPass;
